<template>
  <section
    :class="['cartItem', { 'cartItem--highlight': cartConfirmationObj.visiblity }]"
    v-if="!placeholder"
    role="group"
    :aria-labelledby="'cartItemTitle-' + item.variant_id"
    :aria-describedby="'cartItemDescription-' + item.variant_id"
  >
    <span
      v-if="cartConfirmationObj.visiblity"
      class="cartItem__confirmation"
      role="status"
      aria-live="polite">
        {{ cartConfirmationObj.message}}
    </span>
    <div class="cartItem__outer" :data-id="item.variant_id">
      <ImageField
        class="cartItem__image"
        :obj="{ url: item.image.url, alt: 'cart image', width: 228, height: 285 }"
        width-param="width"
        sizes="300px"
      />
      <div class="cartItem__inner">
        <div class="cartItem__top">
          <div>
            <a
              :href="`/products/` + item.handle + '?variant=' + item.variant_id"
              class="cartItem__title"
              :aria-label="`Product title ${item.product_title}`"
              :id="'cartItemTitle-' + item.variant_id"
              v-text="item.product_title"
            ></a>
            <div
              class="cartItem__options"
              v-if="item.variant_options.length"
              :id="'cartItemDescription-' + item.variant_id"
            >
              <span v-for="item in item.variant_options" :key="item" v-html="formatVariantOption(item)"></span>
            </div>
            <div v-for="(value, key, index) in item.properties" :key="index" class="cartItem__extra">
              <span v-if="!key.startsWith('_')">{{ key }}: {{ value.toString() }}</span>
            </div>
          </div>
        </div>
        <div class="cartItem__middle">
          <div class="cartItem__actions">
            <button
              class="cartItem__action"
              @click.prevent="updateQty(item.line, item.quantity - 1, false)"
              :aria-label="'Decrease quantity of ' + item.product_title + ' by 1'"
            >
              <div class="cartItem__qd">
                <span class="cartItem__qdI"></span>
              </div>
            </button>
            <span
              :class="{ 'cartItem--processing': updating }"
              class="cartItem__qty"
              :aria-label="'Current quantity of ' + item.product_title + ': '"
              role="status"
              aria-live="polite"
              v-text="item.quantity"
            ></span>
            <button
              class="cartItem__action"
              :disabled="disabled"
              @click.prevent="updateQty(item.line, item.quantity + 1, true)"
              :aria-label="'Increase quantity of ' + item.product_title + ' by 1'"
            >
              <div class="cartItem__qi">
                <span class="cartItem__qiI1"></span>
              </div>
              <div class="cartItem__qi">
                <span class="cartItem__qiI2"></span>
              </div>
            </button>
          </div>
          <div class="cartItem__price">
            <span
              v-if="discountPrice"
              :class="[{ 'cartItem__price-value--discount': isDiscount }]"
              v-text="`${discountPrice}`"
              role="text"
              :aria-label="'Discount price of ' + item.product_title + ': ' + discountPrice"
            ></span>
            <span
              v-else
              :class="[{ 'cartItem__price-value--discount': isDiscount }]"
              v-text="`${price}`"
              role="text"
              :aria-label="'Price of ' + item.product_title + ': ' + price"
            ></span>
            <span
              v-if="isDiscount && comparePrice != '$0'"
              class="cartItem__price-value"
              v-text="`${comparePrice}`"
              role="text"
              :aria-label="'Compare price of ' + item.product_title + ': ' + comparePrice"
            ></span>
            <span
              v-else-if="isDiscount && discountPrice"
              class="cartItem__price-value"
              v-text="`${price}`"
              role="text"
              :aria-label="'Discounted price of ' + item.product_title + ': ' + price"
            ></span>
          </div>
        </div>
        <div class="cartItem__bottom">
          <button
            v-if="!item.product_title.includes('E-Gift Card')"
            class="cartItem__bottom-action"
            @click.prevent="toggleEditItem"
            :aria-label="'Edit details of ' + item.product_title"
          >
            Edit
          </button>
          <span
            v-if="!item.product_title.includes('E-Gift Card')"
            class="cartItem__bottom-seperator"
            tabindex="-1"
          >|</span
          >
          <button
            class="cartItem__bottom-action"
            @click.prevent="updateQty(item.line, 0, false)"
            :aria-label="'Remove ' + item.product_title + ' from cart'"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  </section>
  <div v-else class="cartItem__placeholder">
    <div class="cartItem__placeholder-image placeholder"></div>
    <div class="cartItem__placeholder-wrap">
      <div class="cartItem__placeholder-title placeholder"></div>
      <div class="cartItem__placeholder-label placeholder"></div>

      <div class="cartItem__placeholder-actions placeholder"></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed } from 'vue';
  import { useStore } from 'vuex';
  import cartHelpers from '../api';
  import ImageField from '../../global/image.vue';

  const { status } = cartHelpers;
  const store = useStore();

  const props = defineProps({
    cart: {
      type: Array,
      default: () => [],
    },

    item: {
      type: Object,
      default: () => ({}),
    },

    placeholder: {
      type: Boolean,
      default: false,
    },

    cartConfirmationObj: {
      type: Object,
      default: {
        visiblity: false,
        message: 'Item Added To Cart',
      },
    },
  });

  const { item } = toRefs(props);

  const emit = defineEmits(['quantity-change']);

  const updateIds = computed(() => store.state.updateIds);
  const inventory = computed(() => store.state.inventory);
  const cartStatus = computed(() => store.state.status);
  const updateLine = computed(() => store.state.updateLine);

  const updating = computed(() => {
    return updatingLine.value;
  });

  const updatingLine = computed(() => {
    const line = item.value.line;
    return updateLine.value === line && cartStatus.value === status.progress;
  });

  const itemInventory = computed(() => {
    const id = item.value.variant_id;
    return inventory.value.find((variant) => variant.variant_id === id) || {};
  });

  const disabled = computed(() => {
    return itemInventory.value.inventory_quantity <= item.value.quantity;
  });

  const isDiscount = computed(() => {
    if (item.value.price < itemInventory.value.compare_at_price || item.value.price > item.value.discounted_price) {
      return true;
    } else {
      return false;
    }
  });

  const price = computed(() => {
    const _price = item.value.price / 100;
    return window.formattedPrice(_price);
  });

  const comparePrice = computed(() => {
    const _compare_price = itemInventory.value.compare_at_price / 100;
    return window.formattedPrice(_compare_price);
  });

  const discountPrice = computed(() => {
    const _discount_price = item.value.discounted_price / 100;
    return window.formattedPrice(_discount_price);
  });

  function updateQty(line, quantity = 0, option = true) {
    if ((quantity && item.value.quantity < itemInventory.value.inventory_quantity) || !option) {
      emit('quantity-change', { line, quantity });
    }
  }

  function formatVariantOption(option) {
    return window.formattedSize(option);
  }

  const toggleEditItem = () => {
    window.toggleDrawer(true, 'EditCartItem', {
      drawerHeading: 'Edit Item',
      productHandle: item.value.handle,
      variantId: item.value.variant_id,
      line: item.value.line,
      lineKey: item.value.key,
      quantity: item.value.quantity,
    });
  };
</script>
